import { Box, Grid, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  AdminCacheStats,
  CacheHitRateDataPoint,
  StorageGrowthDataPoint,
  CacheOperationsDataPoint,
  TopCachedResource,
} from '../../../types/admin';
import RefreshButton from './shared/RefreshButton';
import AdminMetricCard from '../shared/AdminMetricCard';
import CacheHitRateChart from './cache/CacheHitRateChart';
import StorageGrowthChart from './cache/StorageGrowthChart';
import { formatNumber } from '../../../utils/formatters';

interface CacheManagementTabProps {
  cacheStats: AdminCacheStats;
  hitRateHistory: CacheHitRateDataPoint[];
  storageGrowthHistory: StorageGrowthDataPoint[];
  operationsHistory: CacheOperationsDataPoint[];
  topResources: TopCachedResource[];
  loading?: boolean;
  lastUpdated: string;
  onRefresh: () => void;
  onClearCache: (cacheType?: 'r2' | 'kv' | 'all') => Promise<{ success: boolean }>;
}

export default function CacheManagementTab({
  cacheStats,
  hitRateHistory,
  storageGrowthHistory,
  loading,
  lastUpdated,
  onRefresh,
  onClearCache,
}: CacheManagementTabProps) {
  return (
    <Box>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<DeleteIcon />}
            onClick={() => onClearCache('all')}
            size="small"
          >
            Clear All Caches
          </Button>
        </Box>
        <RefreshButton onRefresh={onRefresh} lastUpdated={lastUpdated} loading={loading} />
      </Box>

      {/* Cache Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Overall Hit Rate"
            value={`${cacheStats.overall_hit_rate}%`}
            subtitle="Cache effectiveness"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="R2 Storage"
            value={`${cacheStats.r2.total_size_gb} GB`}
            subtitle={`${formatNumber(cacheStats.r2.total_objects)} objects`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="KV Hit Rate"
            value={`${cacheStats.kv.hit_rate}%`}
            subtitle={`${formatNumber(cacheStats.kv.total_keys)} keys`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Bandwidth Saved"
            value={`${(cacheStats.bytes_saved / 1e9).toFixed(2)} GB`}
            subtitle="By caching"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={6}>
          <CacheHitRateChart data={hitRateHistory} loading={loading} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <StorageGrowthChart data={storageGrowthHistory} loading={loading} />
        </Grid>
      </Grid>
    </Box>
  );
}
